import React, { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  shorthands,
  Text,
  Card,
  CardHeader,
  Badge,
  Spinner,
  ProgressBar,
  tokens,
  Button,
  Divider } from
'@fluentui/react-components';
import {
  HeartPulse24Regular,
  Database24Regular,
  DataUsage24Regular,
  HardDrive24Regular,
  Server24Regular,
  Checkmark24Filled,
  Warning24Filled,
  ErrorCircle24Filled,
  ArrowClockwise24Regular,
  Info24Regular,
  Clock24Regular,
  ArrowLeftRegular } from
'@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/apiClient';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingVerticalXL,
    maxWidth: '1400px',
    margin: '0 auto'
  },
  header: {
    marginBottom: tokens.spacingVerticalXL,
    ...shorthands.borderBottom('3px', 'solid', '#0047AB'),
    ...shorthands.padding('0', '0', '20px', '0'),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    fontSize: '36px',
    fontWeight: '700',
    color: '#1a1a1a',
    letterSpacing: '-0.5px',
    marginBottom: tokens.spacingVerticalS
  },
  subtitle: {
    fontSize: '16px',
    color: tokens.colorNeutralForeground2
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalL
  },
  card: {
    ...shorthands.padding('20px')
  },
  statusHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px'
  },
  statusIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  metric: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.padding('8px', '0'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  metricLabel: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2
  },
  metricValue: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1
  },
  progressSection: {
    marginTop: '12px'
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px'
  },
  refreshButton: {
    position: 'absolute',
    top: '24px',
    right: '24px'
  },
  healthGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginTop: '16px'
  },
  healthItem: {
    ...shorthands.padding('16px'),
    ...shorthands.borderRadius('8px'),
    backgroundColor: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center'
  },
  healthStatus: {
    fontSize: '24px',
    marginBottom: '8px'
  },
  healthLabel: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '4px'
  },
  healthValue: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginTop: '16px'
  },
  detailItem: {
    display: 'flex',
    justifyContent: 'space-between',
    ...shorthands.padding('8px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('4px')
  }
});

interface SystemHealth {
  status: 'healthy' | 'warning' | 'error';
  postgres: {
    connected: boolean;
    version?: string;
    uptime?: string | {hours?: number;minutes?: number;seconds?: number;milliseconds?: number;};
    activeConnections?: number;
    maxConnections?: number;
  };
  database: {
    tableCount?: number;
    rowCounts?: Record<string, number>;
    totalSize?: string;
    largestTable?: {name: string;size: string;};
  };
  container: {
    memory: {
      used: number;
      total: number;
      percentage: number;
    };
    disk: {
      used: number;
      total: number;
      percentage: number;
    };
    cpu: {
      usage: number;
      cores: number;
    };
    uptime: string;
    hostname?: string;
    platform?: string;
    nodeVersion?: string;
  };
  services: {
    teamsApi: boolean;
    lsegFeed: boolean;
    driftMonitor: boolean;
    auditLogging: boolean;
  };
  lastCheck: string;
}

export const SystemStatus: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Helper function to handle loading state updates
  const updateLoadingStates = (isRefresh: boolean, isLoading: boolean) => {
    if (isRefresh) {
      setRefreshing(isLoading);
    } else {
      setLoading(isLoading);
    }
  };

  // Helper function to handle API response
  const handleHealthResponse = (response: any) => {
    const data = response?.data || response;
    setHealth(data as SystemHealth);
    setError(null);
  };

  // Helper function to handle API errors
  const handleHealthError = (err: any) => {
    console.error('Failed to load system health:', err);
    setError(err.message || 'Failed to load system health');
  };

  const loadSystemHealth = useCallback(async (isRefresh = false) => {
    try {
      updateLoadingStates(isRefresh, true);
      setError(null);

      const response = await apiClient.get('/system/health');
      handleHealthResponse(response);
    } catch (err: any) {
      handleHealthError(err);
    } finally {
      updateLoadingStates(isRefresh, false);
    }
  }, []);

  useEffect(() => {
    loadSystemHealth();
    // Refresh every 30 seconds
    const interval = setInterval(loadSystemHealth, 30000);
    return () => clearInterval(interval);
  }, [loadSystemHealth]);

  const formatBytes = (bytes: number): string => {
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor(seconds % 86400 / 3600);
    const minutes = Math.floor(seconds % 3600 / 60);

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const formatPostgresUptime = (uptime: string | {hours?: number;minutes?: number;seconds?: number;milliseconds?: number;} | undefined): string => {
    if (!uptime) {
      return 'Unknown';
    }

    if (typeof uptime === 'object') {
      const hours = (uptime as any).hours || 0;
      const minutes = (uptime as any).minutes || 0;
      return `${hours}h ${minutes}m`;
    }

    return uptime;
  };

  const getUsageColor = (percentage: number | undefined): 'error' | 'warning' | 'success' => {
    const value = percentage || 0;
    if (value > 90) return 'error';
    if (value > 70) return 'warning';
    return 'success';
  };

  // Helper function to get database connection info
  const getDatabaseConnectionInfo = (): string => {
    const active = health?.postgres.activeConnections || 0;
    const max = health?.postgres.maxConnections || 100;
    return `${active} / ${max}`;
  };

  // Helper function to get database table count
  const getDatabaseTableCount = (): string => {
    return String(health?.database.tableCount || 0);
  };

  // Helper function to get database version
  const getDatabaseVersion = (): string => {
    return health?.postgres.version || 'Unknown';
  };

  // Helper function to get database size
  const getDatabaseSize = (): string => {
    return health?.database.totalSize || 'Unknown';
  };

  // Helper function to render service health items
  const renderServiceHealthItem = (
  service: keyof SystemHealth['services'],
  label: string,
  connectedText: string,
  disconnectedText: string) =>
  {
    const isConnected = health?.services[service];
    return (
      <div className={styles.healthItem} data-testid="system-status-health-item-div">
        <div className={styles.healthStatus} data-testid="system-status-health-status-div">
          {isConnected ?
          <Checkmark24Filled style={{ color: tokens.colorPaletteGreenForeground1 }} data-testid="system-status-service-health-checkmark24-filled" /> :
          <ErrorCircle24Filled style={{ color: tokens.colorPaletteRedForeground1 }} data-testid="system-status-service-health-error-circle24-filled" />
          }
        </div>
        <Text className={styles.healthLabel} data-testid="system-status-service-health-label-text">{label}</Text>
        <Text className={styles.healthValue} data-testid="system-status-service-health-value-text">
          {isConnected ? connectedText : disconnectedText}
        </Text>
      </div>);

  };

  // Helper function to render metric rows
  const renderMetricRow = (label: string, value: string) =>
  <div className={styles.metric} data-testid="system-status-metric-div">
        <Text className={styles.metricLabel} data-testid="system-status-metric-label-text">{label}</Text>
        <Text className={styles.metricValue} data-testid="system-status-metric-value-text">{value}</Text>
    </div>;


  // Helper function to render loading state
  const renderLoadingState = () =>
  <div className={styles.container} data-testid="system-status-container-div">
      <Spinner size="large" label="Loading system health..." data-testid="system-status-loading-system-health-spinner" />
    </div>;


  // Helper function to render error state
  const renderErrorState = () =>
  <div className={styles.container} data-testid="system-status-container-2-div">
        <Card className={styles.card} data-testid="system-status-error-state-card">
        <Text data-testid="system-status-error-loading-system-health-text">Error loading system health: {error}</Text>
        <Button
        appearance="primary"
        onClick={() => loadSystemHealth()}
        style={{ marginTop: '16px' }} data-testid="system-status-retry-button">

          Retry
        </Button>
      </Card>
    </div>;


  // Helper function to render usage card (Memory/Disk)
  const renderUsageCard = (
  type: 'memory' | 'disk',
  icon: React.ReactElement,
  title: string,
  data: {used: number;total: number;percentage: number;}) =>
  {
    const unitLabel = type === 'memory' ? 'MB' : 'GB';

    return (
      <Card className={styles.card} data-testid={`system-status-${type}-usage-card`}>
        <CardHeader
          image={icon}
          header={<Text weight="semibold" data-testid={`system-status-${type}-usage-title-text`}>{title}</Text>}
          description={`${data.percentage.toFixed(1) || 0}% utilized`} data-testid={`system-status-${type}-usage-card-header`} />

        <div style={{ borderBottom: '1px solid #e0e0e0', margin: '0 -24px', width: 'calc(100% + 48px)' }} data-testid="system-status-usage-card-divider-div" />
        <div style={{ marginTop: '16px' }} data-testid="system-status-usage-card-content-div">
          <div className={styles.progressSection} data-testid="system-status-progress-section-div">
            <div className={styles.progressLabel} data-testid="system-status-progress-label-div">
              <Text size={200} data-testid="system-status-used-text">Used</Text>
              <Text size={200} weight="semibold" data-testid={`system-status-${type}-used-value-text`}>
                {health ? formatBytes(data.used) : `0 ${unitLabel}`} / {health ? formatBytes(data.total) : `0 ${unitLabel}`}
              </Text>
            </div>
            <ProgressBar
              value={data.percentage || 0}
              max={100}
              thickness="large"
              color={getUsageColor(data.percentage)} data-testid="system-status-progress-bar" />

          </div>
          <div className={styles.metric} style={{ marginTop: '16px' }} data-testid="system-status-metric-2-div">
            <Text className={styles.metricLabel} data-testid="system-status-available-text">Available</Text>
            <Text className={styles.metricValue} data-testid={`system-status-${type}-available-value-text`}>
              {health ? formatBytes(data.total - data.used) : `0 ${unitLabel}`}
            </Text>
          </div>
        </div>
      </Card>);

  };

  // Helper function to render overall system status card
  const renderSystemStatusCard = () =>
    <Card className={styles.card} style={{ marginBottom: '24px' }} data-testid="system-status-overall-status-card">
      <div className={styles.statusHeader} data-testid="system-status-status-header-div">
        <div className={styles.statusIcon} data-testid="system-status-status-icon-div">
          {health && getStatusIcon(health.status)}
          <Text size={500} weight="semibold" data-testid="system-status-system-status-text">
            System Status
          </Text>
        </div>
        <Badge
        appearance="filled"
        color={health ? getStatusColor(health.status) : 'informative'} data-testid="system-status-overall-status-badge">

          {health?.status.toUpperCase() || 'UNKNOWN'}
        </Badge>
      </div>
      <Text size={200} data-testid="system-status-last-updated-text">
        Last updated: {health ? new Date(health.lastCheck).toLocaleString() : 'Never'}
      </Text>
    </Card>;


  if (loading && !health) {
    return renderLoadingState();
  }

  if (error && !health) {
    return renderErrorState();
  }

  return (
    <div className={styles.container} data-testid="system-status-container-3-div">
      <div className={styles.header} data-testid="system-status-header-div">
        <div data-testid="system-status-header-info-div">
          <div className={styles.title} data-testid="system-status-title-div">
            <HeartPulse24Regular data-testid="system-status-heart-pulse24-regular" />
            <Text size={600} weight="semibold" data-testid="system-status-health-text">System Status & Health</Text>
          </div>
          <Text className={styles.subtitle} data-testid="system-status-real-time-monitoring-of-database-container-resources-text">
            Real-time monitoring of database, container resources, and service health
          </Text>
        </div>
        <Button
          appearance="secondary"
          icon={<ArrowLeftRegular data-testid="system-status-arrow-left-regular" />}
          onClick={() => navigate('/settings')} data-testid="system-status-back-to-settings-button">

          Back to Settings
        </Button>
      </div>

      <div style={{ position: 'relative' }} data-testid="system-status-refresh-container-div">
        <Button
          className={styles.refreshButton}
          appearance="subtle"
          icon={<ArrowClockwise24Regular data-testid="system-status-arrow-clockwise24-regular" />}
          onClick={() => loadSystemHealth(true)}
          disabled={refreshing} data-testid="system-status-button">

          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {/* Overall System Status */}
      {renderSystemStatusCard()}

      <div className={styles.grid} data-testid="system-status-grid-div">
        {/* Database Status */}
        <Card className={styles.card} data-testid="system-status-database-card">
          <CardHeader
            image={<Database24Regular data-testid="system-status-database24-regular" />}
            header={<Text weight="semibold" data-testid="system-status-postgre-sql-database-text">PostgreSQL Database</Text>}
            description={
            <Badge
              appearance="filled"
              color={health?.postgres.connected ? 'success' : 'danger'}
              size="small" data-testid="system-status-database-connection-badge">

                {health?.postgres.connected ? 'Connected' : 'Disconnected'}
              </Badge>
            } data-testid="system-status-database-card-header" />

          <Divider data-testid="system-status-database-divider" />
          <div style={{ marginTop: '16px' }} data-testid="system-status-database-metrics-div">
            {renderMetricRow('Version', getDatabaseVersion())}
            {renderMetricRow('Uptime', formatPostgresUptime(health?.postgres.uptime))}
            {renderMetricRow('Connections', getDatabaseConnectionInfo())}
            {renderMetricRow('Tables', getDatabaseTableCount())}
            {renderMetricRow('Total Size', getDatabaseSize())}
          </div>
        </Card>

        {/* Memory Usage */}
        {health && renderUsageCard(
          'memory',
          <DataUsage24Regular data-testid="system-status-data-usage24-regular" />,
          'Memory Usage',
          health.container.memory
        )}

        {/* Disk Usage */}
        {health && renderUsageCard(
          'disk',
          <HardDrive24Regular data-testid="system-status-hard-drive24-regular" />,
          'Disk Usage',
          health.container.disk
        )}

        {/* Container Info */}
        <Card className={styles.card} data-testid="system-status-container-info-card">
          <CardHeader
            image={<Server24Regular data-testid="system-status-server24-regular" />}
            header={<Text weight="semibold" data-testid="system-status-container-information-text">Container Information</Text>}
            description="Runtime environment details" data-testid="system-status-container-info-card-header" />

          <Divider data-testid="system-status-container-info-divider" />
          <div style={{ marginTop: '16px' }} data-testid="system-status-container-metrics-div">
            {renderMetricRow('Hostname', health?.container.hostname || 'Unknown')}
            {renderMetricRow('Platform', health?.container.platform || 'Unknown')}
            {renderMetricRow('Node Version', health?.container.nodeVersion || 'Unknown')}
            {renderMetricRow('Uptime', health?.container.uptime || 'Unknown')}
            {renderMetricRow('CPU Cores', String(health?.container.cpu.cores || 0))}
            {renderMetricRow('CPU Usage', `${health?.container.cpu.usage.toFixed(1) || 0}%`)}
          </div>
        </Card>
      </div>

      {/* Service Health Status */}
      <Card className={styles.card} data-testid="system-status-service-health-card">
        <CardHeader
          header={<Text weight="semibold" size={400} data-testid="system-status-service-health-text">Service Health</Text>}
          description="Status of integrated services and APIs" data-testid="system-status-service-health-card-header" />

        <div className={styles.healthGrid} data-testid="system-status-health-grid-div">
          {renderServiceHealthItem('teamsApi', 'Teams API', 'Connected', 'Disconnected')}
          {renderServiceHealthItem('lsegFeed', 'Domain Feed', 'Connected', 'Disconnected')}
          {renderServiceHealthItem('driftMonitor', 'Drift Monitor', 'Running', 'Stopped')}
          {renderServiceHealthItem('auditLogging', 'Audit Logging', 'Active', 'Inactive')}
        </div>
      </Card>

      {/* Database Tables Info */}
      {health?.database.rowCounts &&
        <Card className={styles.card} data-testid="system-status-database-tables-card">
          <CardHeader
          header={<Text weight="semibold" size={400} data-testid="system-status-database-tables-text">Database Tables</Text>}
          description="Row counts and storage details" data-testid="system-status-database-tables-card-header" />

          <div className={styles.detailsGrid} data-testid="system-status-details-grid-div">
            {Object.entries(health.database.rowCounts).map(([table, count]) =>
          <div key={table} className={styles.detailItem} data-testid="system-status-detail-item-div">
                <Text size={200} data-testid="system-status-database-table-name-text">{table}</Text>
                <Text size={200} weight="semibold" data-testid="system-status-rows-text">{count} rows</Text>
              </div>
          )}
          </div>
          {health.database.largestTable &&
        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: tokens.colorNeutralBackground2, borderRadius: '6px' }} data-testid="system-status-largest-table-container-div">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} data-testid="system-status-largest-table-info-div">
                <Info24Regular style={{ color: tokens.colorBrandForeground1 }} data-testid="system-status-info24-regular" />
                <Text size={200} data-testid="system-status-largest-table-text">
                  Largest table: <strong data-testid="system-status-largest-table-name-strong">{health.database.largestTable.name}</strong> ({health.database.largestTable.size})
                </Text>
              </div>
            </div>
        }
        </Card>
      }
    </div>);

};

export const getStatusIcon = (status: 'healthy' | 'warning' | 'error') => {
  switch (status) {
    case 'healthy':
      return <Checkmark24Filled style={{ color: tokens.colorPaletteGreenForeground1 }} data-testid="system-status-overall-checkmark24-filled" />;
    case 'warning':
      return <Warning24Filled style={{ color: tokens.colorPaletteYellowForeground1 }} data-testid="system-status-warning24-filled" />;
    case 'error':
      return <ErrorCircle24Filled style={{ color: tokens.colorPaletteRedForeground1 }} data-testid="system-status-overall-error-circle24-filled" />;
  }
};

export const getStatusColor = (status: 'healthy' | 'warning' | 'error') => {
  switch (status) {
    case 'healthy':
      return 'success';
    case 'warning':
      return 'warning';
    case 'error':
      return 'danger';
  }
};

export default SystemStatus;